import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth-store';
import { api } from '@/lib/api';

/**
 * Discover what events are available in this GA4 property
 * Useful to see what conversion tracking is actually implemented
 */
export function useGA4AvailableEvents(limit = 50) {
  const { activePropertyId, properties } = useAuthStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const ga4PropertyId = activeProperty?.ga4Property?.id?.replace('properties/', '') || activePropertyId;

  return useQuery({
    queryKey: ['ga4-available-events', ga4PropertyId],
    queryFn: async () => {
      if (!ga4PropertyId || !activeProperty?.ga4Property) {
        return null;
      }

      try {
        const request = {
          propertyId: ga4PropertyId,
          dateRange: { startDate: '28daysAgo', endDate: 'yesterday' },
          dimensions: ['eventName'],
          metrics: ['eventCount'],
          orderBy: { metric: 'eventCount', desc: true },
          limit,
        };

        const response = await api.google.runGAReport(request);

        if (!response?.rows) {
          return [];
        }

        return response.rows.map((row: any) => ({
          eventName: row.dimensionValues?.[0]?.value || '',
          eventCount: parseInt(row.metricValues?.[0]?.value || '0', 10),
        }));
      } catch (error) {
        console.error('Failed to fetch GA4 available events:', error);
        return [];
      }
    },
    enabled: !!ga4PropertyId && !!activeProperty?.ga4Property,
    staleTime: 1000 * 60 * 10, // 10 minutes - events don't change often
  });
}

interface EcommerceFunnelData {
  sessions: number;
  productViews?: number;
  addToCarts?: number;
  checkouts?: number;
  purchases?: number;
  // Conversion rates
  viewToCartRate?: number;
  cartToCheckoutRate?: number;
  checkoutToPurchaseRate?: number;
  overallConversionRate?: number;
}

/**
 * Fetch GA4 e-commerce funnel data
 * Requires e-commerce events to be implemented: view_item, add_to_cart, begin_checkout, purchase
 */
export function useGA4EcommerceFunnel() {
  const { activePropertyId, properties } = useAuthStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const ga4PropertyId = activeProperty?.ga4Property?.id?.replace('properties/', '') || activePropertyId;

  return useQuery({
    queryKey: ['ga4-ecommerce-funnel', ga4PropertyId],
    queryFn: async (): Promise<EcommerceFunnelData | null> => {
      if (!ga4PropertyId || !activeProperty?.ga4Property) {
        return null;
      }

      try {
        // Fetch sessions
        const sessionsRequest = {
          propertyId: ga4PropertyId,
          dateRange: { startDate: '28daysAgo', endDate: 'yesterday' },
          metrics: ['sessions'],
        };

        // Fetch e-commerce event counts
        const ecommerceRequest = {
          propertyId: ga4PropertyId,
          dateRange: { startDate: '28daysAgo', endDate: 'yesterday' },
          dimensions: ['eventName'],
          metrics: ['eventCount'],
          dimensionFilter: {
            filter: {
              fieldName: 'eventName',
              inListFilter: {
                values: ['view_item', 'add_to_cart', 'begin_checkout', 'purchase'],
              },
            },
          },
        };

        const [sessionsResponse, ecommerceResponse] = await Promise.all([
          api.google.runGAReport(sessionsRequest),
          api.google.runGAReport(ecommerceRequest),
        ]);

        const sessions = parseInt(sessionsResponse?.rows?.[0]?.metricValues?.[0]?.value || '0', 10);

        // Parse e-commerce events
        let productViews: number | undefined;
        let addToCarts: number | undefined;
        let checkouts: number | undefined;
        let purchases: number | undefined;

        if (ecommerceResponse?.rows) {
          for (const row of ecommerceResponse.rows) {
            const eventName = row.dimensionValues?.[0]?.value;
            const eventCount = parseInt(row.metricValues?.[0]?.value || '0', 10);

            switch (eventName) {
              case 'view_item':
                productViews = eventCount;
                break;
              case 'add_to_cart':
                addToCarts = eventCount;
                break;
              case 'begin_checkout':
                checkouts = eventCount;
                break;
              case 'purchase':
                purchases = eventCount;
                break;
            }
          }
        }

        // Calculate conversion rates
        const viewToCartRate = productViews && addToCarts ? (addToCarts / productViews) * 100 : undefined;
        const cartToCheckoutRate = addToCarts && checkouts ? (checkouts / addToCarts) * 100 : undefined;
        const checkoutToPurchaseRate = checkouts && purchases ? (purchases / checkouts) * 100 : undefined;
        const overallConversionRate = sessions && purchases ? (purchases / sessions) * 100 : undefined;

        return {
          sessions,
          productViews,
          addToCarts,
          checkouts,
          purchases,
          viewToCartRate,
          cartToCheckoutRate,
          checkoutToPurchaseRate,
          overallConversionRate,
        };
      } catch (error) {
        console.error('Failed to fetch GA4 e-commerce funnel:', error);
        // Return sessions only if e-commerce events fail (property may not have e-commerce tracking)
        return null;
      }
    },
    enabled: !!ga4PropertyId && !!activeProperty?.ga4Property,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Fetch GA4 e-commerce revenue metrics
 */
export function useGA4EcommerceRevenue() {
  const { activePropertyId, properties } = useAuthStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const ga4PropertyId = activeProperty?.ga4Property?.id?.replace('properties/', '') || activePropertyId;

  return useQuery({
    queryKey: ['ga4-ecommerce-revenue', ga4PropertyId],
    queryFn: async () => {
      if (!ga4PropertyId || !activeProperty?.ga4Property) {
        return null;
      }

      try {
        // Current period
        const currentRequest = {
          propertyId: ga4PropertyId,
          dateRange: { startDate: '28daysAgo', endDate: 'yesterday' },
          metrics: ['totalRevenue', 'transactions', 'averagePurchaseRevenue'],
        };

        // Previous period for comparison
        const previousRequest = {
          propertyId: ga4PropertyId,
          dateRange: { startDate: '56daysAgo', endDate: '29daysAgo' },
          metrics: ['totalRevenue', 'transactions', 'averagePurchaseRevenue'],
        };

        const [currentResponse, previousResponse] = await Promise.all([
          api.google.runGAReport(currentRequest),
          api.google.runGAReport(previousRequest),
        ]);

        if (!currentResponse?.rows?.[0]) {
          return null;
        }

        const current = currentResponse.rows[0].metricValues || [];
        const previous = previousResponse?.rows?.[0]?.metricValues || [];

        const getValue = (values: Array<{ value: string }>, index: number): number => {
          const val = parseFloat(values[index]?.value || '0');
          return isNaN(val) ? 0 : val;
        };

        const calcChange = (curr: number, prev: number): number => {
          if (prev === 0) return curr > 0 ? 100 : 0;
          return ((curr - prev) / prev) * 100;
        };

        const totalRevenue = getValue(current, 0);
        const transactions = getValue(current, 1);
        const avgOrderValue = getValue(current, 2);

        const prevRevenue = getValue(previous, 0);
        const prevTransactions = getValue(previous, 1);
        const prevAOV = getValue(previous, 2);

        return {
          totalRevenue,
          transactions,
          avgOrderValue,
          revenueChange: calcChange(totalRevenue, prevRevenue),
          transactionsChange: calcChange(transactions, prevTransactions),
          aovChange: calcChange(avgOrderValue, prevAOV),
        };
      } catch (error) {
        console.error('Failed to fetch GA4 e-commerce revenue:', error);
        return null;
      }
    },
    enabled: !!ga4PropertyId && !!activeProperty?.ga4Property,
    staleTime: 1000 * 60 * 5,
  });
}
